import styled from 'styled-components'
import PropTypes from 'prop-types'
import { cloneElement, useMemo, useState } from 'react'

import { base } from '../../mixins'
import { SG, SLR, PB70T } from '../../colors'
import { Box } from '../../layout/box'
import { marginPropTypes, paddingPropTypes } from '../../utils'

const colors = {
  default: PB70T,
  success: SG,
  error: SLR
}
const Wrap = styled(Box)`
  ${base};
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  margin-bottom: 15px;
`
const Label = styled.label`
  display: flex;
  flex-direction: column;
`
const LabelText = styled.span`
  color: black;
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 3px 0;

  &::after {
    content: '*';
    color: ${SLR};
    position: relative;
    left: 4px;
    display: ${({ isRequired }) => (isRequired ? 'inline' : 'none')};
  }
`
const InputWrap = styled.span`
  &&&& {
    input,
    textarea,
    [data-select-toggle],
    [data-select-menu] {
      ${({ status }) =>
        status !== 'default' ? `border-color: ${colors[status]}` : null};
    }
  }
`
const Description = styled.span`
  display: block;
  margin: 5px 0 0 0;
  font-size: 12px;
  color: ${({ status }) => colors[status]};
`
/**
 * The Field component is used to wrap form fields that require additional meta data like labeling and validation.
 */
const Field = ({
  id,
  htmlFor,
  children,
  label = '',
  isVisible = true,
  isRequired = false,
  desc = '',
  status = 'default',
  width = 'auto',
  ...rest
}) => {
  const [describedById] = useState(
    `field-${id || Math.round(Math.random() * 1000000)}-desc`
  )
  const childElements = useMemo(() => {
    let baseAttrs = {
      'aria-required': isRequired
    }

    if (desc) {
      baseAttrs = { ...baseAttrs, 'aria-describedby': describedById }
    }

    return baseAttrs
  }, [desc, describedById, isRequired])
  const childElement = cloneElement(children, childElements)

  return (
    <Wrap width={width} isVisible={isVisible} {...rest}>
      {htmlFor ? (
        <>
          <Label id={id} htmlFor={htmlFor}>
            <LabelText isRequired={isRequired}>{label}</LabelText>
          </Label>
          <InputWrap as="div" status={status}>
            {childElement}
          </InputWrap>
        </>
      ) : (
        <Label id={id}>
          <LabelText isRequired={isRequired}>{label}</LabelText>
          <InputWrap status={status}>{childElement}</InputWrap>
        </Label>
      )}
      {desc && (
        <Description id={describedById} status={status}>
          {desc}
        </Description>
      )}
    </Wrap>
  )
}

Field.propTypes = {
  ...marginPropTypes,
  ...paddingPropTypes,
  /** The label for the form field */
  label: PropTypes.string,
  /** Whether the form field should be visible or not */
  isVisible: PropTypes.bool,
  /** Whether the form field is required */
  isRequired: PropTypes.bool,
  /** Description of validation for the form field */
  desc: PropTypes.string,
  /** The validation status of the form field  */
  status: PropTypes.oneOf(['default', 'success', 'error']),
  /** The id of the field label */
  id: PropTypes.string,
  /** The id of the form element the field label describes */
  htmlFor: PropTypes.string,
  /** The width of the form field */
  width: PropTypes.string
}

export { Field }
